import {
  createSuccessResult,
  createErrorResult,
  Result,
  ResultError,
} from '../types/result'

interface FieldValidationData<Values> {
  validationFn(arg0: unknown): Result<string>
  fieldName: keyof Values
  fieldElement: HTMLElement | null
}

export function createFormValidator<Values>(
  values: Values,
  setError: (field: keyof Values, error: string) => void,
  // This is a function because the fieldElement refs are undefined
  // when the validator is created.
  fieldValidationData: () => FieldValidationData<Values>[]
): (updatedFieldName?: keyof Values) => Result<(HTMLElement | null)[]> {
  const fieldValidator = ({
    validationFn,
    fieldName,
    fieldElement,
    updatedFieldName,
  }: FieldValidationData<Values> & {
    updatedFieldName?: keyof Values
  }): Result<HTMLElement | null> => {
    if (updatedFieldName && updatedFieldName !== fieldName) {
      return createSuccessResult()
    }

    const validationResult = validationFn(values[fieldName])
    if (validationResult.success) {
      setError(fieldName, '')
      return createSuccessResult()
    }

    setError(fieldName, validationResult.error)
    return createErrorResult(fieldElement)
  }

  return function validateForm(updatedFieldName) {
    const results = fieldValidationData().map((data) =>
      fieldValidator({ ...data, updatedFieldName })
    )

    const errorResults = results.filter(
      (result) => !result.success
    ) as ResultError<HTMLElement | null>[]

    const errors = errorResults.map((result) => {
      return result.error
    })

    if (errors.length === 0) {
      return createSuccessResult()
    }

    if (!updatedFieldName) {
      errors[0]?.focus()
    }

    return createErrorResult(errors)
  }
}
